import React from 'react'
import { Coding, QuestionnaireItem } from '@medplum/fhirtypes'
import { Rule } from 'antd/es/form'
import { DEFAULT_REQUIRED_FIELD_MESSAGE } from 'components/Assessments/constants'
import {
  QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL,
  QUESTIONNAIRE_SCORING_TOOLTIP_EXTENSION_URL
} from 'fhir/Questionnaire/constants'
import { CodingSelectOption, CodingValue, RAW_DIAGNOSIS_FIELDS, RawDiagnosis } from './types'
import { ValidateFormValuesType } from '../hooks/useValidateAndUpdateFormValues'

export type { CodingValue } from './types'

// Extracts secondary text (description) from questionnaire field extensions
export function extractSecondaryText(field?: QuestionnaireItem): string | undefined {
  return field?.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.display
}

// Builds form validation rules combining default required rule with custom rules
export function buildFormRules(
  required: boolean,
  fieldRequired: boolean | undefined,
  customRules: Rule[] = []
): Rule[] {
  return [
    {
      required: Boolean(required && fieldRequired),
      message: DEFAULT_REQUIRED_FIELD_MESSAGE
    },
    ...customRules
  ]
}

// Converts string code from Select to CodingValue object for form storage
// Preserves codes even if not found in current options (e.g., due to pagination/search)
export function createSelectValueFromEvent(
  options: CodingSelectOption[]
): (code: string) => CodingValue | undefined {
  return (code: string): CodingValue | undefined => {
    if (typeof code === 'string' && code.trim() !== '') {
      const foundOption = options.find((item) => item.value === code)
      if (foundOption) {
        return {
          code: foundOption.code,
          display: foundOption.display
        }
      }
      return {
        code: code,
        display: code
      }
    }
    return undefined
  }
}

// Extracts code from CodingValue object for Select display
export function createSelectValueProps(options: CodingSelectOption[]): (
  value: CodingValue | undefined
) => {
  value: string | undefined
} {
  return (value: CodingValue | undefined): { value: string | undefined } => {
    if (value && typeof value === 'object' && 'code' in value) {
      const code = String(value.code)
      const foundOption = options.find(
        (item) =>
          item.value.toLowerCase() === code.toLowerCase() ||
          item.code.toLowerCase() === code.toLowerCase()
      )
      return { value: foundOption?.value ?? code }
    }
    return { value: undefined }
  }
}

// Converts array of string codes from Select to array of CodingValue objects for form storage
// Preserves codes even if not found in current options (e.g., due to pagination/search)
export function createMultiSelectValueFromEvent(
  options: CodingSelectOption[]
): (codes: string[]) => CodingValue[] {
  return (codes: string[]): CodingValue[] => {
    if (!Array.isArray(codes)) {
      return []
    }
    return codes
      .map((code) => {
        if (typeof code === 'string' && code.trim() !== '') {
          const foundOption = options.find(
            (item) => item.value.toLowerCase() === code.toLowerCase()
          )
          if (foundOption) {
            return {
              code: foundOption.code,
              display: foundOption.display
            }
          }
          // Preserve code even if not in current options (prevents data loss with pagination/search)
          return {
            code: code,
            display: code
          }
        }
        return null
      })
      .filter((item): item is CodingValue => item !== null)
  }
}

// Extracts array of codes from array of CodingValue objects for Select display
export function createMultiSelectValueProps(options: CodingSelectOption[]): (
  value: CodingValue[] | undefined
) => {
  value: string[]
} {
  return (value: CodingValue[] | undefined): { value: string[] } => {
    if (Array.isArray(value)) {
      const codes = value
        .map((item) => {
          if (typeof item === 'object' && item !== null && 'code' in item) {
            const code = String(item.code)
            const foundOption = options.find(
              (option) =>
                option.value.toLowerCase() === code.toLowerCase() ||
                option.code.toLowerCase() === code.toLowerCase()
            )
            // If found, use the option's value (which matches the option structure)
            // If not found (e.g., due to search filtering), use the code as-is
            // The code should match the option's value format (which is also the code)
            return foundOption?.value ?? code
          }
          return null
        })
        .filter((code): code is string => code !== null)
      return { value: codes }
    }
    return { value: [] }
  }
}

// Converts string code from RadioField to CodingValue object for form storage
export function createRadioValueFromEvent(
  answerOptions?: QuestionnaireItem['answerOption']
): (e: React.ChangeEvent<HTMLInputElement> | string) => CodingValue | undefined {
  return (e: React.ChangeEvent<HTMLInputElement> | string): CodingValue | undefined => {
    const code = typeof e === 'string' ? e : e?.target?.value ?? e
    if (typeof code === 'string' && code.trim() !== '') {
      const foundOption = answerOptions?.find((option) => option?.valueCoding?.code === code)
      const valueCoding = foundOption?.valueCoding
      if (valueCoding?.code) {
        return {
          code: valueCoding.code,
          display: valueCoding.display || valueCoding.code
        }
      }
    }
    return undefined
  }
}

// Extracts code string from CodingValue object for RadioField display
export function createRadioValueProps(): (value: CodingValue | undefined) => {
  value: string | undefined
} {
  return (value: CodingValue | undefined): { value: string | undefined } => {
    return { value: value?.code }
  }
}

// Converts array of string codes from Select to array of CodingValue objects for form storage
// Works with QuestionnaireItem answerOption directly (for additional questions)
export function createMultiSelectValueFromAnswerOptions(
  answerOptions?: QuestionnaireItem['answerOption']
): (codes: string[]) => CodingValue[] {
  return (codes: string[]): CodingValue[] => {
    if (!codes || !Array.isArray(codes)) {
      return []
    }
    return codes
      .map((code) => {
        if (typeof code === 'string' && code.trim() !== '') {
          const option = answerOptions?.find((opt) => opt?.valueCoding?.code === code)
          return {
            code,
            display: option?.valueCoding?.display || code
          }
        }
        return null
      })
      .filter((item): item is CodingValue => item !== null)
  }
}

// Extracts array of codes from array of CodingValue objects for Select display
// Works with QuestionnaireItem answerOption directly (for additional questions)
export function createMultiSelectValuePropsFromAnswerOptions(): (
  value: CodingValue[] | undefined
) => {
  value: string[]
} {
  return (value: CodingValue[] | undefined): { value: string[] } => {
    if (!value || !Array.isArray(value)) {
      return { value: [] }
    }
    return {
      value: value.map((item) => item.code).filter(Boolean)
    }
  }
}

// Type guard to validate RawDiagnosis array structure
export function isRawDiagnosisArray(value: unknown): value is RawDiagnosis[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item): item is RawDiagnosis =>
        typeof item === 'object' &&
        item !== null &&
        RAW_DIAGNOSIS_FIELDS.ICD10_CODE in item &&
        RAW_DIAGNOSIS_FIELDS.DESCRIPTION in item &&
        typeof item[RAW_DIAGNOSIS_FIELDS.ICD10_CODE] === 'string' &&
        typeof item[RAW_DIAGNOSIS_FIELDS.DESCRIPTION] === 'string'
    )
  )
}

export function parseScoringTooltipMarkdown(field: QuestionnaireItem): string | undefined {
  const extension = field.extension?.find(
    (ext) => ext.url === QUESTIONNAIRE_SCORING_TOOLTIP_EXTENSION_URL
  )
  const s = extension?.valueString
  return typeof s === 'string' && s.trim() !== '' ? s : undefined
}

// Extract helper texts from field extensions
export function parseHelperTexts(field: QuestionnaireItem): string[] {
  const extension = field.extension?.find(
    (ext) => ext.url === QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL
  )
  return (
    extension?.valueCodeableConcept?.coding
      ?.map((code) => code.display)
      .filter((display): display is string => Boolean(display)) || []
  )
}

// Type-safe field text accessor
export function getFieldText(field: QuestionnaireItem): string {
  return typeof field.text === 'string' ? field.text : ''
}

// Type-safe field linkId accessor
export function getFieldLinkId(field: QuestionnaireItem): string {
  return typeof field.linkId === 'string' ? field.linkId : ''
}

// Detect detailed mode based on helper texts and prefix
export function detectDetailedMode(
  helperTexts: string[],
  prefix: string | undefined | null,
  forcedMode?: boolean
): boolean {
  return forcedMode ?? (helperTexts.length > 1 && prefix !== undefined && prefix !== null)
}

// Type guard for ValidateFormValuesType
export function isValidateFormValuesType(value: unknown): value is ValidateFormValuesType {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const obj = value as Record<string, unknown>

  return (
    'type' in obj &&
    'linkId' in obj &&
    'text' in obj &&
    'value' in obj &&
    typeof obj.type === 'string' &&
    typeof obj.linkId === 'string' &&
    typeof obj.text === 'string'
  )
}

// Type guard for Coding
export function isCodingValue(value: unknown): value is Coding {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const obj = value as Record<string, unknown>

  return 'code' in obj && typeof obj.code === 'string'
}

// Converts Coding object to CodingValue format expected by form components
export function codingToCodingValue(coding: Coding): CodingValue {
  return {
    code: coding.code || '',
    display: coding.display || coding.code || ''
  }
}

// Extracts code string from form value
export function extractCodeFromFormValue(formValue: unknown): string | undefined {
  if (typeof formValue === 'string') {
    return formValue
  }
  if (typeof formValue === 'object' && formValue !== null && 'code' in formValue) {
    const codeValue = formValue['code']
    return typeof codeValue === 'string' ? codeValue : undefined
  }
  return undefined
}

// Extract description and option descriptions from helper texts
// Description is always extracted from the first helper text
// Option descriptions are only extracted in detailed mode
export function extractDescriptionAndOptions(
  helperTexts: string[],
  isDetailedMode: boolean
): { description: string; optionDescriptions: string[] } {
  return {
    description: helperTexts[0] || '',
    optionDescriptions: isDetailedMode ? helperTexts.slice(1) || [] : []
  }
}

// Check if section incomplete message should be displayed
export function shouldShowSectionIncompleteMessage(
  isDisabledQuestionWithSectionReference: boolean,
  isSectionForItemComplete: boolean | null
): boolean {
  return isDisabledQuestionWithSectionReference && isSectionForItemComplete === false
}

// Check if section reference link should be displayed
export function shouldShowSectionReferenceLink(
  isDisabledQuestionWithSectionReference: boolean,
  sectionReferenceId: string | undefined
): boolean {
  return Boolean(isDisabledQuestionWithSectionReference && sectionReferenceId)
}
